import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, TimePicker } from 'antd';
import FormItem from "components/fields/FormItem";
import { connect, useSelector } from "react-redux";
import { 
	getLeaveDetails, 
	doLeaveCreate, 
} from "../../../../redux/leave/leaveState";
import CrudButtons from "components/button/crudbuttons";
import { DATE_FORMAT, MODULE_LEAVE } from "variables/constants";
import { requiredField } from "utils/crudSagaHelper";
import SwitchField from "components/fields/SwitchField";
import TextArea from "antd/es/input/TextArea";

const LeaveForm = ({
	getRecord, 
	doCreate, 
	doUpdate, 
	recordData, 
	requesting, 
	success, 
	onCancel,
	submitLabel,
	...props
}) => {
	
  	const { recordId, mode } = props;  	
  	const [form] = Form.useForm();
	const authUser = useSelector((state) => state.auth.user);
	const { RangePicker } = DatePicker;
	const [formData, setFormData] = useState({
		lve_user_id: null,
		lve_date_range: [],
		lve_is_undertime: false,
		lve_undertime_from: null,
		lve_undertime_to: null,
	});
	const [computedDays, setComputedDays] = useState(0);
	const [computedHours, setComputedHours] = useState(0);
	
  	useEffect(() => {
        getRecord(recordId);
	}, [getRecord, recordId]);
    
  	const onSubmit = (formData) => {	
		if (!recordData?.lve_id) {
			formData.lve_status_id = 70; 
		}

    	try {  
			if (formData.lve_date_range?.length === 2) {
				formData.lve_date_from = formData.lve_date_range[0].format(`${DATE_FORMAT}`);
				formData.lve_date_to = formData.lve_date_range[1].format(`${DATE_FORMAT}`);
			}
			delete formData.lve_date_range;

			if (formData.lve_is_undertime) {
				if (formData.lve_undertime_from) {
					formData.lve_undertime_from = formData.lve_undertime_from.format('HH:mm:ss');
				}
				if (formData.lve_undertime_to) {
					formData.lve_undertime_to = formData.lve_undertime_to.format('HH:mm:ss');
				}
			}

			if (!formData.lve_user_id) {
				formData.lve_user_id = authUser?.id;
			}
			recordData.lve_id > 0 ? doUpdate(formData): doCreate(formData); 
    	} catch (error) {
      		console.error('Save failed:', error);
    	}
  	};

	const calculateUndertimeHours = (timeFrom, timeTo) => {
		if (timeFrom && timeTo) {
			const diffHours = timeTo.diff(timeFrom, 'hour', true);
			setComputedHours(Math.max(0, diffHours));
		} else {
			setComputedHours(0);
		}
	};
  
  	return (
		
    	<Form form={form}
			className={mode !== "view" ? "form" : ""}
			layout="vertical"
			onFinish={onSubmit}
		>   
			<FormItem
				name="lve_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>

			<FormItem
				name="lve_status_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>

			<div className="grid grid-cols-3 gap-y-2 gap-x-4">         		            	            
				{authUser?.user_level === 1 && mode !== "view" && (
					<FormItem
						className="font-medium mb-2"
						label="Employee"
						required
					>
						<FormItem
							name="lve_user_id"
							noStyle
							rules={[requiredField]}
							initialValue={formData?.lve_user_id ?? recordData?.lve_user_id ?? null}
						>
							<Select
								showSearch
								allowClear
								className="input-field"
								placeholder="Select Employee"
								onChange={(value) =>
									setFormData((prev) => ({ ...prev, lve_user_id: value }))
								}
								value={formData?.lve_user_id ?? recordData?.lve_user_id ?? undefined}
								options={recordData?.employeeList}
								optionFilterProp="label"
							/>
						</FormItem>
					</FormItem>
				)}  
				
				<FormItem className="font-medium mb-2"
					name="lve_type_id" 
					label="Leave Type"
					rules={[requiredField]}
				>
					<Select id="lve_type_id"
						allowClear={true}
						showSearch
						className="input-field"
						options={recordData?.leaveTypeList}
						placeholder="Select leave type"
					/>
				</FormItem>

				<FormItem
					className="font-medium mb-2"
					name="lve_date_range" 
					label="Date Range"
					rules={[requiredField]}
				>
					<RangePicker
						className="input-field w-full"
						format={DATE_FORMAT}
						onChange={(dates) => {
							setFormData((prev) => ({ ...prev, lve_date_range: dates }));

							if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
								setComputedDays(0);
								return;
							}

							const days = dates[1].diff(dates[0], "day") + 1;
							setComputedDays(days);
						}}
						placeholder={['Start date', 'End date']}
					/>
				</FormItem>

				<div className="col-span-1 flex gap-4">
					{!formData.lve_is_undertime && (
						<FormItem 
							label="Count" 
							className="font-medium mb-2 w-1/2"
						>
							<Input 
								value={computedDays}
								readOnly 
								className="input-field" 
								placeholder="0"
							/>
						</FormItem>
					)}

					<FormItem 
						label="Is Undertime"
						name="lve_is_undertime"
						className="font-medium mb-2 w-1/2"
					>
						<SwitchField
							id="lve_is_undertime"
							mode="edit"
							type="text"
							labelYes="Yes"
							labelNo="No"
							onChange={(value) => {
								setFormData((prev) => ({ ...prev, lve_is_undertime: value }));
								if (value) {
									if (computedDays > 1) {
										const singleDate = formData.lve_date_range?.[0];
										if (singleDate) {
											form.setFieldsValue({ 
												lve_date_range: [singleDate, singleDate]
											});
											setComputedDays(1);
										}
									}
								} else {
									setComputedHours(0);
									form.setFieldsValue({ 
										lve_undertime_from: null, 
										lve_undertime_to: null 
									});
								}
							}}
						/>
					</FormItem>
				</div>

				{formData.lve_is_undertime && (
					<>
						<div className="col-span-2">
							<div className="grid grid-cols-3 gap-4">
								<FormItem
									className="font-medium mb-2"
									name="lve_undertime_from"
									label="Scheduled Time In"
									rules={[requiredField]}
								>
									<TimePicker
										id="lve_undertime_from"
										className="input-field w-full"
										format="HH:mm"
										onChange={(time) => {
											setFormData((prev) => ({ ...prev, lve_undertime_from: time }));
											calculateUndertimeHours(time, formData.lve_undertime_to);
										}}
										placeholder="Select time"
									/>
								</FormItem>

								<FormItem
									className="font-medium mb-2"
									name="lve_undertime_to"
									label="Actual Time Out"
									rules={[requiredField]}
								>
									<TimePicker
										id="lve_undertime_to"
										className="input-field w-full"
										format="HH:mm"
										onChange={(time) => {
											setFormData((prev) => ({ ...prev, lve_undertime_to: time }));
											calculateUndertimeHours(formData.lve_undertime_from, time);
										}}
										placeholder="Select time"
									/>
								</FormItem>

								<FormItem 
									label="Undertime Hours" 
									className="font-medium mb-2 w-[120px]"
								>
									<Input 
										value={`${computedHours} hour(s)`}
										readOnly 
										className="input-field" 
										placeholder="0 hour(s)"
									/>
								</FormItem>
							</div>
						</div>
					</>
				)}

				<FormItem 
					label="Is Unpaid"
					name="lve_is_unpaid"
					className="font-medium mb-2"
				>
					<SwitchField
						id="lve_is_unpaid"
						mode="edit"
						type="text"
						labelYes="Yes"
						labelNo="No"
					/>
				</FormItem>

				<FormItem
					className="font-medium mb-2 col-span-3"
					name="lve_reason"
					label="Reason"
					rules={[requiredField]}
				>
					<TextArea
						id="lve_reason"
						className="input-field"
						maxLength={255}
						placeholder={formData.lve_is_undertime ? "Please provide reason for undertime..." : "Please provide reason for leave..."}
					/>
				</FormItem>
			</div>
			
			<div className="mt-4">
				{(mode === "edit") && (
					<CrudButtons 
						mode={mode} 
						recordId={recordData?.lve_id ?? 0} 
						isModal={true}
						modalId={recordData?.lve_id > 0 ? 'edit_leave' : 'add_leave'}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel="Submit"
						moduleId={MODULE_LEAVE}
					/>
				)}
			</div> 				 			
		</Form>	 	
  	);
};

const mapStateToProps = (state) => ({
  	recordData: state.leave.leaveDetails?.data,
  	requesting: state.leave.leaveDetails?.requesting,
  	success: state.leave.leaveDetails?.succcess,
});

const mapDispatchToProps = (dispatch) => ({
	getRecord: (id) => dispatch(getLeaveDetails(id)),
  	doCreate: (data) => dispatch(doLeaveCreate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveForm);